import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ChevronDown, Users, BarChart3, Layers } from 'lucide-react';
import { toast } from '../hooks/use-toast';

interface InteractiveMessageProps {
  msg: any;
  onOptionSelect?: (optionId: string, optionText: string) => void;
}

interface ButtonData {
  text: string;
  id?: string;
  params?: any;
}

interface ListData {
  type: 'list';
  title?: string;
  description?: string;
  buttonText?: string;
  sections: Array<{
    title?: string;
    rows?: Array<{
      id?: string;
      title?: string;
      description?: string;
    }>;
  }>;
}

interface PollData {
  type: 'poll';
  question?: string;
  options: string[];
  selectableCount?: number;
}

interface CarouselData {
  type: 'carousel';
  cards: Array<{
    title?: string;
    description?: string;
    image?: string;
    buttons?: ButtonData[];
  }>;
}

const getButtonText = (button: ButtonData): string => {
  return button.params?.display_text || button.text;
};

const InteractiveMessage: React.FC<InteractiveMessageProps> = ({ msg, onOptionSelect }) => {
  let interactiveData: any = null;

  // Tentar fazer parse dos dados interativos
  try {
    if (msg.dadosInterativos) {
      interactiveData = typeof msg.dadosInterativos === 'string'
        ? JSON.parse(msg.dadosInterativos)
        : msg.dadosInterativos;
    }
  } catch (error) {
    console.error('Erro ao fazer parse dos dados interativos:', error);
  }

  // Se não houver dados interativos, mostrar como mensagem de texto normal
  if (!interactiveData) {
    return (
      <div className="text-gray-800 dark:text-gray-100 break-words whitespace-pre-wrap">
        {msg.conteudo}
      </div>
    );
  }

  // Renderizar mensagens do tipo BUTTON
  if (interactiveData.type === 'button') {
    const buttons: ButtonData[] = interactiveData.buttons || [];

    return (
      <div className="w-full">
        {/* Texto principal da mensagem */}
        {msg.conteudo && (
          <p className="text-gray-800 dark:text-gray-100 mb-3 break-words whitespace-pre-wrap">
            {msg.conteudo}
          </p>
        )}

        {/* Botões interativos */}
        <div className="flex flex-col gap-2">
          {buttons.map((button, index) => (
            <Button
              key={index}
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 border-gray-200 dark:border-slate-600"
              onClick={() => {
                if (button.params?.url) {
                  window.open(button.params.url, '_blank', 'noopener,noreferrer');
                } else if (button.params?.phone_number) {
                  window.open(`tel:${button.params.phone_number}`, '_self');
                } else if (button.params?.flow_cta) {
                  toast({
                    title: "WhatsApp Flow",
                    description: "Esta interação só pode ser realizada no WhatsApp",
                  });
                } else if (onOptionSelect) {
                  onOptionSelect(button.id || `button_${index}`, getButtonText(button));
                } else {
                  toast({
                    title: "Opção Selecionada",
                    description: getButtonText(button),
                  });
                }
              }}
            >
              <span className="text-sm">{getButtonText(button)}</span>
            </Button>
          ))}
        </div>

        {/* Footer se houver */}
        {interactiveData.footer && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
            {interactiveData.footer}
          </p>
        )}
      </div>
    );
  }

  // Renderizar mensagens do tipo LIST
  if (interactiveData.type === 'list') {
    const listData: ListData = interactiveData;

    return (
      <div className="w-full">
        {/* Título e descrição */}
        {msg.conteudo && (
          <p className="text-gray-800 dark:text-gray-100 mb-3 break-words whitespace-pre-wrap">
            {msg.conteudo}
          </p>
        )}

        {listData.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            {listData.description}
          </p>
        )}

        {/* Seções da lista */}
        <div className="space-y-3">
          {listData.sections?.map((section, sectionIndex) => (
            <div key={sectionIndex} className="border border-gray-200 dark:border-slate-600 rounded-lg">
              {section.title && (
                <div className="bg-gray-50 dark:bg-slate-700 px-3 py-2 border-b border-gray-200 dark:border-slate-600">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {section.title}
                  </h4>
                </div>
              )}

              <div className="divide-y divide-gray-200 dark:divide-slate-600">
                {section.rows?.map((row, rowIndex) => (
                  <button
                    key={rowIndex}
                    className="w-full text-left px-3 py-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              onClick={() => {
                if (onOptionSelect) {
                  onOptionSelect(row.id || `list_${sectionIndex}_${rowIndex}`, row.title || '');
                      } else {
                        toast({
                          title: "Opção Selecionada",
                          description: row.title,
                        });
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {row.title}
                        </p>
                        {row.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                            {row.description}
                          </p>
                        )}
                      </div>
                      <ChevronDown className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Botão da lista */}
        {listData.buttonText && (
          <Button
            variant="outline"
            className="w-full mt-3 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600"
            onClick={() => {
              toast({
                title: "Menu",
                description: listData.buttonText,
              });
            }}
          >
            <Users className="h-4 w-4 mr-2" />
            {listData.buttonText}
          </Button>
        )}
      </div>
    );
  }

  // Renderizar mensagens do tipo POLL
  if (interactiveData.type === 'poll') {
    const pollData: PollData = interactiveData;

    return (
      <div className="w-full">
        {/* Pergunta da enquete */}
        {msg.conteudo && (
          <p className="text-gray-800 dark:text-gray-100 mb-4 font-medium break-words whitespace-pre-wrap">
            {msg.conteudo}
          </p>
        )}

        {/* Opções da enquete */}
        <div className="space-y-2">
          {pollData.options?.map((option, index) => (
            <Button
              key={index}
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 border-gray-200 dark:border-slate-600"
              onClick={() => {
                if (onOptionSelect) {
                  onOptionSelect(`poll_option_${index}`, option);
                } else {
                  toast({
                    title: "Voto Registrado",
                    description: option,
                  });
                }
              }}
            >
              <div className="flex items-center w-full">
                <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-slate-500 mr-3" />
                <span className="text-sm flex-1 text-left">{option}</span>
              </div>
            </Button>
          ))}
        </div>

        {/* Informações da enquete */}
        <div className="flex items-center justify-between mt-3 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center">
            <BarChart3 className="h-3 w-3 mr-1" />
            {pollData.selectableCount && pollData.selectableCount > 1
              ? `Escolha até ${pollData.selectableCount} opções`
              : 'Escolha 1 opção'
            }
          </div>
        </div>
      </div>
    );
  }

  // Renderizar mensagens do tipo CAROUSEL
  if (interactiveData.type === 'carousel') {
    const carouselData: CarouselData = interactiveData;

    // Normalizar cards do carrossel (suporta formato UAZAPI aninhado e formato plano)
    const normalizedCards: Array<{ image: string; title: string; description: string; buttons: Array<{ text: string; id: string }> }> = (carouselData.cards || []).map((card: any) => {
      const thumbnail = card.header?.Media?.ImageMessage?.JPEGThumbnail;
      const image = thumbnail
        ? (thumbnail.startsWith('data:') ? thumbnail : `data:image/jpeg;base64,${thumbnail}`)
        : card.image
        || card.header?.Media?.ImageMessage?.URL
        || '';
      const bodyText = card.description
        || card.body?.text
        || card.title
        || '';
      const lines = bodyText.split('\n').filter(Boolean);
      const title = lines[0]?.replace(/\*+/g, '').trim() || '';
      const description = bodyText.replace(/\*+/g, '').trim();

      // Extrair botões (formato UAZAPI aninhado ou plano)
      const rawButtons = card.buttons || card.InteractiveMessage?.NativeFlowMessage?.buttons || [];
      const buttons = rawButtons.map((btn: any) => {
        let text = btn.text || '';
        let id = btn.id || '';
        if (btn.buttonParamsJSON) {
          try {
            const parsed = typeof btn.buttonParamsJSON === 'string'
              ? JSON.parse(btn.buttonParamsJSON)
              : btn.buttonParamsJSON;
            if (parsed.display_text) text = parsed.display_text;
            if (parsed.id) id = parsed.id;
          } catch (e) { /* ignore */ }
        }
        return { text, id };
      }).filter((btn: any) => btn.text);

      return { image, title, description, buttons };
    });

    return (
      <div className="w-full">
        {/* Texto principal */}
        {msg.conteudo && (
          <p className="text-gray-800 dark:text-gray-100 mb-3 break-words whitespace-pre-wrap">
            {msg.conteudo}
          </p>
        )}

        {/* Cards do carrossel */}
        <div className="space-y-3">
          {normalizedCards.map((card, index) => (
            <div key={index} className="border border-gray-200 dark:border-slate-600 rounded-lg overflow-hidden bg-white dark:bg-slate-700">
              {/* Imagem do card */}
              {card.image && (
                <img
                  src={card.image}
                  alt={card.title || `Card ${index + 1}`}
                  className="w-full h-32 object-cover"
                />
              )}

              {/* Conteúdo do card */}
              <div className="p-3">
                {card.title && (
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {card.title}
                  </h4>
                )}

                {card.description && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 whitespace-pre-wrap">
                    {card.description}
                  </p>
                )}

                {/* Botões do card */}
                {card.buttons && card.buttons.length > 0 && (
                  <div className="space-y-2">
                    {card.buttons.map((button: { text: string; id?: string }, btnIndex) => (
                      <Button
                        key={btnIndex}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start h-8 bg-white dark:bg-slate-600 hover:bg-gray-50 dark:hover:bg-slate-500 border-gray-200 dark:border-slate-500"
                        onClick={() => {
                          if (onOptionSelect) {
                            onOptionSelect(button.id || `carousel_${index}_btn_${btnIndex}`, button.text);
                          } else {
                            toast({
                              title: "Opção Selecionada",
                              description: button.text,
                            });
                          }
                        }}
                      >
                        <span className="text-xs">{button.text}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Indicador do carrossel */}
        <div className="flex items-center justify-center mt-3 space-x-1">
          <Layers className="h-3 w-3 text-gray-400" />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {normalizedCards.length} itens
          </span>
        </div>
      </div>
    );
  }

  // Fallback para tipos desconhecidos
  return (
    <div className="w-full">
      <Badge variant="outline" className="mb-2">
        Mensagem Interativa
      </Badge>
      <p className="text-gray-800 dark:text-gray-100 break-words whitespace-pre-wrap">
        {msg.conteudo}
      </p>
    </div>
  );
};

export default InteractiveMessage;
